import { useState, useRef, useEffect } from "react"
import { Clock, ChevronRight } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { FullPageDotsLoader } from "@/clientWeb/components/ui/page-loader"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/clientWeb/components/ui/select"
import { useNavigate } from "react-router-dom"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { useMeetingsStore } from "../../store/meetings.store"
import { meetingsApi } from "../../services/meetings.api"
import { useMeetingSessionStore } from "@/features/meeting/store/meeting-session.store"
import { useAuthStore } from "@/features/auth/store/auth.store"

interface InstantMeetingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const LICENSES = [
  { id: "INVCCCP_30", name: "INVCCCP_30", seats: 30 },
  { id: "INVCCCP_50", name: "INVCCCP_50", seats: 50 },
  { id: "INVCCCP_100", name: "INVCCCP_100", seats: 100 },
]

const DURATIONS = [
  { value: "15", label: "15 Mins" },
  { value: "30", label: "30 Mins" },
  { value: "45", label: "45 Mins" },
  { value: "60", label: "1 Hour" },
]

export default function InstantMeetingDialog({ open, onOpenChange }: InstantMeetingDialogProps) {
  const t = useTranslation()
  const navigate = useNavigate()
  const fetchFromApi = useMeetingsStore((s) => s.fetchFromApi)
  const user = useAuthStore((s) => s.user)

  const defaultName = user ? `${user.name}'s Meeting` : "My Meeting"

  const [meetingName, setMeetingName] = useState(defaultName)
  const [selectedLicense, setSelectedLicense] = useState(LICENSES[0])
  const [seats, setSeats] = useState(2)
  const [duration, setDuration] = useState("30")
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  // The dialog stays mounted between opens (`open` only toggles its
  // visibility), so without this, clearing the name and closing left the
  // empty string sitting in state for the next open to inherit. Reset to the
  // default whenever the dialog transitions back to open.
  useEffect(() => {
    if (open) {
      setMeetingName(defaultName)
      setErrors({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Guarded by a ref, not by `submitting`.
  //
  // `setSubmitting(true)` is a state update, so the button's `disabled` only
  // takes effect on the next render. Two clicks landing in the same tick — a
  // double-click, or a click racing the Enter key — both read `submitting` as
  // false and both POST, which is how one press produced two meetings. A ref
  // changes synchronously, so the second call sees the guard already up.
  const startingRef = useRef(false)

  const handleStartMeeting = async () => {
    if (startingRef.current) return
    startingRef.current = true
    setErrors({})
    setSubmitting(true)
    try {
      const data = await meetingsApi.createInstant({
        title: meetingName,
        duration_minutes: parseInt(duration, 10),
        seats,
      })

      useMeetingSessionStore.getState().setRoomCredentials(data.roomCredentials)
      void fetchFromApi()

      // No "Meeting started" toast: the host pressed Start and is being taken
      // into the meeting, so telling them it started (and landed in their
      // list) only covered the preview screen with news they already had.
      onOpenChange(false)
      navigate(`/meeting/${data.meeting.meetingId ?? data.meeting.id}?name=${encodeURIComponent(user?.name ?? 'You')}`)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : ''
      toast.error({
        title: t('meetings.couldNotStartMeeting'),
        description: msg || undefined,
      })
    } finally {
      startingRef.current = false
      setSubmitting(false)
    }
  }

  return (
    <>
    {submitting && <FullPageDotsLoader message={t("meetings.startingMeeting")} />}
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] sm:max-w-[480px] p-0 overflow-hidden bg-card dark:bg-card border-0 shadow-2xl"
        style={{ borderRadius: "16px" }}
      >
        {/* Header */}
        <DialogHeader className="p-3 sm:p-4 border-b border-stroke-100 dark:border-border dark:border-slate-800">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg sm:text-xl font-semibold text-slate-800 dark:text-white">
              {t('meetings.instantMeeting')}
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Form Content */}
        <div className="px-4 sm:px-6 space-y-2">
          {/* Meeting Name */}
          <div>
            <label className="text-xs font-medium text-slate-400 dark:text-foreground tracking-wide mb-2 block">
              {t('meetings.meetingName')}
            </label>
            <Input
              value={meetingName}
              onChange={(e) => { setMeetingName(e.target.value.slice(0, 50)); setErrors(p => ({ ...p, meetingName: '' })) }}
              variant="outlined"
              className="text-base sm:text-lg font-medium"
              maxLength={50}
              error={!!errors.meetingName}
              errorText={errors.meetingName}
            />
            <div className={`text-[11px] text-right mt-1 ${meetingName.length >= 50 ? 'text-destructive' : 'text-muted-foreground'}`}>
              {meetingName.length}/50
            </div>
          </div>

          {/* Select License */}
          <div>
            <label className="text-xs font-medium text-slate-400 dark:text-foreground tracking-wide mb-2 block">
              {t('meetings.selectLicense')}
            </label>
            <Select
              value={selectedLicense.id}
              onValueChange={(value) => {
                const license = LICENSES.find(l => l.id === value)
                if (license) {
                  setSelectedLicense(license)
                  setSeats(Math.min(seats, license.seats))
                }
              }}
            >
              <SelectTrigger variant="outlined" className="font-medium">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LICENSES.map((license) => (
                  <SelectItem key={license.id} value={license.id}>
                    {license.name} ({license.seats} seats)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* No. of Seats */}
          <div className="flex flex-col gap-1">
            <div className="flex w-full items-center justify-between dark:border-border">
              <div className="flex flex-1 text-sm font-medium dark:text-slate-200">
                {t('meetings.noOfSeats')} ({selectedLicense.seats} {t('meetings.seatsAvailable')})
              </div>
              <div>
                <Input
                  type="number"
                  min={2}
                  max={selectedLicense.seats}
                  value={seats}
                  onChange={(e) => { setSeats(Math.min(Math.max(parseInt(e.target.value) || 2, 2), selectedLicense.seats)); setErrors(p => ({ ...p, seats: '' })) }}
                  className="w-16 flex text-center text-xl font-semibold border-slate-300 dark:border-slate-600"
                />
              </div>
            </div>
            {errors.seats && <p className="text-xs text-error-600 dark:text-error-500">{errors.seats}</p>}
          </div>

          {/* Duration */}
          <div>
            <label className="text-xs font-medium text-slate-400 dark:text-foreground tracking-wide mb-2 block">
              {t('meetings.duration')}
            </label>
            <Select value={duration} onValueChange={(v) => { setDuration(v); setErrors(p => ({ ...p, duration: '' })) }}>
              <SelectTrigger variant="outlined" className="font-medium" leftIcon={<Clock className="w-5 h-5 text-slate-400" />}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATIONS.map((dur) => (
                  <SelectItem key={dur.value} value={dur.value}>
                    {dur.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.duration && <p className="text-xs text-error-600 dark:text-error-500">{errors.duration}</p>}
          </div>

          {/* Advance Link — carries the whole configuration, not just the
              name. Seats and duration used to be left behind, so raising the
              seat count here and then opening the advanced screen found it
              back at that screen's default of 2, to be set a second time.
              Seats travel as a number: the two screens spell the same licence
              tiers differently (INVCCCP_30 here, Invccp_30 there). */}
          <div className="pt-1">
            <Button variant={'ghost'} onClick={()=> navigate('/schedule', { state: { prefillName: meetingName, prefillSeats: seats, prefillDuration: duration } })} className="text-primary dark:text-foreground" endIcon={<ChevronRight className="w-4 h-4" />}>
              {t('meetings.advance')}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-t border-stroke-100 dark:border-border dark:border-slate-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleStartMeeting}
            disabled={!meetingName.trim() || submitting}
            className="px-4 sm:px-6 py-2.5 h-auto rounded-xl bg-primary hover:bg-primary/90 text-white font-medium shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-200 order-1 sm:order-2"
          >
            {submitting ? t('common.starting') : t('meetings.startMeeting')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  )
}
